import { api } from '../../lib/api';
import type { TrackSummary } from '../tracks/TrackList';

/**
 * The album endpoints, in one place.
 *
 * `collections` on the wire, "album" everywhere a person can see - the same
 * split §1.3 draws between the schema's words and the product's. The type is
 * `album` here because that is the only one with a UI; EPs, singles and
 * playlists are the same table and will reuse all of this.
 */
export interface Album {
  id: number;
  type: 'album' | 'ep' | 'single' | 'playlist';
  title: string;
  slug: string;
  description: string | null;
  visibility: 'public' | 'unlisted' | 'private';
  artwork: string | null;
  artwork_small: string | null;
  track_count: number;
  created_at: string;
  owner: { id: number; username: string; display_name: string } | null;
}

export interface AlbumDetail extends Album {
  is_owner: boolean;
  tracks: (TrackSummary & { position: number })[];
}

export interface AlbumDraft {
  title: string;
  description?: string | null;
  visibility?: Album['visibility'];
}

export const albums = {
  list: (username: string) => api.get<{ items: Album[] }>(`/users/${username}/collections`),

  get: (id: number | string) => api.get<AlbumDetail>(`/collections/${id}`),

  create: (draft: AlbumDraft) => api.post<Album>('/collections', draft),

  update: (id: number, draft: Partial<AlbumDraft>) => api.patch<Album>(`/collections/${id}`, draft),

  remove: (id: number) => api.delete<{ deleted: boolean }>(`/collections/${id}`),

  addTrack: (id: number, trackId: number) =>
    api.post<{ added: boolean; track_count: number }>(`/collections/${id}/tracks`, { track_id: trackId }),

  removeTrack: (id: number, trackId: number) =>
    api.delete<{ removed: boolean; track_count: number }>(`/collections/${id}/tracks/${trackId}`),

  /**
   * The whole running order, not a move instruction.
   *
   * The server takes the complete list because a relative "move 3 to 7" has to
   * be interpreted against a state the client may no longer share with it.
   */
  reorder: (id: number, trackIds: number[]) =>
    api.put<{ reordered: boolean }>(`/collections/${id}/tracks`, { track_ids: trackIds }),
};
